import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { cn } from '@Utils/cn';
import { useSearchFieldValues } from '@Hooks/useOmniSearch';

/**
 * Searchable single-select for a Meili facet field. Type-ahead is server-side (Meili
 * `searchForFacetValues`), so it stays fully searchable even for high-cardinality fields.
 */
export function FacetCombobox({
  index,
  field,
  label,
  value,
  onChange,
  className,
}: {
  index: string;
  field: string;
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Only fetch while the dropdown is open; re-query on each debounced keystroke.
  const { data, isFetching } = useSearchFieldValues(index, field, debounced, open);
  const items = data?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn('w-full justify-between overflow-hidden', !value && 'text-muted-foreground', className)}
        >
          <span className="truncate">{value || `Any ${label.toLowerCase()}`}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search ${label.toLowerCase()}…`}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isFetching ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="size-4 animate-spin text-zinc-400" />
                </div>
              ) : (
                'No values found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__any"
                onSelect={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
              >
                <Check className={cn('mr-2 size-4', !value ? 'opacity-100' : 'opacity-0')} />
                Any
              </CommandItem>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={() => {
                    onChange(item.value);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 size-4', value === item.value ? 'opacity-100' : 'opacity-0')} />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
